import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { Label } from './Label';
import { InputWrapper, ErrorText } from './DefaultInput';

export interface TextBoxProps {
  id: string;
  label?: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  type?: string;
  value?: string;
  required?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  hint?: string;
}

export const InputInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
`;

export const StyledTextBox = styled.textarea`
  font-family: 'Akkurat';
  border: 1px solid ${Colors.background};
  color: ${Colors.grayDark};
  background-color: ${Colors.background};
  font-size: 1rem;
  padding: 1rem;
  z-index: 2;
  margin: 0;
  flex: 1;
  resize: none;
  overflow: hidden;
  
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${Colors.gray};
  }

  &:focus {
    outline: none;
  }
  
  &.local-error {
    font-size: 0.75rem; 
    padding-left: 1rem;
    border-color: ${Colors.error};
    color: ${Colors.error};
    
    ::placeholder,
    ::-webkit-input-placeholder {
    color: ${Colors.error};
   }
  }
`;

export const TextBox: FC<TextBoxProps> = (props) => {
  const { value, required = false, errorMessage = 'Bitte das Feld füllen', hasError = false, id, hint, placeholder, onChange, label } = props;
  const textboxRef = useRef<HTMLTextAreaElement | null>(null);
  const inputMinHeight = 128;
  const [inputHeight, setInputHeight] = useState<number>(inputMinHeight);
  const [localError, setLocalError] = useState<boolean>(hasError);

  const resizeInputBox = () => {
    if (textboxRef.current) {
      const height = textboxRef.current.scrollHeight;
      setInputHeight(Math.max(inputMinHeight, height));
    }
  };

  useEffect(() => resizeInputBox(), []);

  useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (value && value.length > 0) {
      setLocalError(false);
    }
  }, [value]);

  const handleLocalError = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (required && event.target.value === '') {
      setLocalError(true);
    } else {
      setLocalError(false);
    }
  };

  return (
    <InputWrapper>
      {
        props.label &&
        <Label for={id} hint={hint}>{label}</Label>
      }
      <InputInnerWrapper>
        <StyledTextBox
          id={id}
          ref={textboxRef}
          className={`${localError ? 'local-error' : ''}`}
          style={{ minHeight: `${inputHeight}px` }}
          placeholder={`${placeholder ? placeholder : 'Hier eingeben'}`}
          rows={1}
          value={value}
          onBlur={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            handleLocalError(event);
          }}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            handleLocalError(event);
            onChange(event);
            resizeInputBox();
          }} />
        {localError && <ErrorText>{errorMessage}</ErrorText>}
      </InputInnerWrapper>
    </InputWrapper>
  );
};
